<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '🏪 Apa itu Toggle F&B & Retail?',
    icon: 'ri-store-3-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        PintuKasir bisa dipakai untuk 2 jenis bisnis: <strong>F&amp;B</strong> (restoran,
        coffee shop, kafe — dine-in, meja, dapur) dan <strong>Retail</strong> (toko
        kelontong, minimarket, apotek — jual barang eceran). Kedua fitur ini diatur
        lewat <strong>2 toggle terpisah</strong>, bukan pilihan salah satu.
      </p>
      <VAlert type="success" variant="tonal" class="mb-4">
        💡 Boleh aktifkan <strong>keduanya sekaligus</strong> (mode hybrid) — misalnya toko
        yang jual kopi (F&amp;B) sekaligus jual snack kemasan eceran (Retail). Mengaktifkan
        salah satu <strong>tidak menghapus atau menyembunyikan</strong> fitur yang satunya
        yang sudah dipakai.
      </VAlert>
      <p class="mb-0">
        Menu ini ada di <strong>Pengaturan → tab "Tipe Bisnis"</strong>, dan hanya bisa
        diubah oleh Owner (atau role dengan izin khusus mengatur fitur tenant).
      </p>
    `,
  },
  {
    title: '⚙️ Cara Mengaktifkan',
    icon: 'ri-toggle-line',
    color: 'success',
    content: `
      <ol class="mb-4">
        <li class="mb-2">Buka menu <strong>Pengaturan</strong> lewat sidebar kiri</li>
        <li class="mb-2">Klik tab <strong>"Tipe Bisnis"</strong></li>
        <li class="mb-2">Nyalakan switch <strong>"Aktifkan Fitur Retail"</strong> — switch
          <strong>"Aktifkan Fitur F&amp;B"</strong> boleh tetap menyala berdampingan</li>
        <li class="mb-0">Klik <strong>"Simpan Tipe Bisnis"</strong></li>
      </ol>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Toggle</th><th>Kalau Aktif</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Aktifkan Fitur F&amp;B</strong></td>
            <td>Munculkan Self-Order, KDS (Kitchen Display), Denah Meja, Modifier, dan
              Resep/HPP</td>
          </tr>
          <tr>
            <td><strong>Aktifkan Fitur Retail</strong></td>
            <td>Munculkan scan barcode di kasir, harga grosir bertingkat, dan tracking
              kedaluwarsa (laporan Stok Kedaluwarsa)</td>
          </tr>
        </tbody>
      </table>
      <VAlert type="warning" variant="tonal">
        ⚠️ Kalau <strong>kedua toggle dimatikan</strong>, sistem tetap mengizinkan (untuk
        jaga-jaga skenario khusus) — tapi ini biasanya tidak disengaja. POS Kasir dan menu
        umum lainnya (Produk, Inventory, Laporan dasar) tetap jalan normal apa pun kondisi
        kedua toggle ini.
      </VAlert>
    `,
  },
  {
    title: '🔄 Bukan Mode Switch',
    icon: 'ri-shield-check-line',
    color: 'info',
    content: `
      <p class="mb-4">
        Poin paling penting: mengaktifkan/menonaktifkan salah satu toggle
        <strong>HANYA mengatur menu mana yang tampil di sidebar</strong> — data yang
        sudah ada (meja, resep, produk dengan barcode, tier grosir, dsb) tidak pernah
        dihapus atau berubah, sekalipun togglenya sedang dimatikan.
      </p>
      <p class="mb-0">
        Contoh: kalau Anda sempat mengisi barcode & tier grosir untuk sebuah produk, lalu
        toggle Retail dimatikan sementara, data barcode & tier tetap tersimpan — cuma
        menu-menunya yang sementara tidak tampil di sidebar. Nyalakan lagi togglenya, dan
        semuanya kembali muncul apa adanya.
      </p>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide/retail">
      <VBtn
        variant="text"
        prepend-icon="ri-arrow-left-line"
        class="mb-2"
      >
        Kembali ke Fitur Retail
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar
        color="primary"
        variant="tonal"
        size="64"
        rounded="lg"
        class="me-4"
      >
        <VIcon
          icon="ri-store-3-line"
          size="36"
        />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Toggle F&B & Retail
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Aktifkan fitur Retail berdampingan dengan F&amp;B, atau salah satu saja
        </p>
      </div>
    </div>

    <VExpansionPanels
      variant="accordion"
      multiple
    >
      <VExpansionPanel
        v-for="(section, index) in sections"
        :key="index"
      >
        <VExpansionPanelTitle>
          <VIcon
            :icon="section.icon"
            :color="section.color"
            class="me-3"
          />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      ❓ Pertanyaan yang Sering Ditanyakan
    </h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Siapa yang boleh mengubah toggle ini?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Hanya user dengan izin mengatur fitur tenant (biasanya Owner). Kalau tab "Tipe
          Bisnis" tidak muncul di menu Pengaturan Anda, berarti role Anda belum punya
          izin tersebut — hubungi Owner toko Anda.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apakah menonaktifkan Retail akan menghapus barcode/tier grosir yang sudah diisi?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak. Data tetap tersimpan utuh, hanya menu-menunya yang sementara tidak
          tampil di sidebar selama togglenya mati.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Toko saya jual kopi sekaligus snack kemasan — toggle mana yang harus dipakai?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Aktifkan keduanya (F&amp;B <em>dan</em> Retail) — ini justru skenario hybrid
          yang memang didukung. Anda akan melihat menu F&amp;B (Denah Meja, KDS, dst)
          <em>dan</em> menu Retail (scan barcode, harga grosir, kedaluwarsa) sekaligus.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      🔗 Halaman Terkait
    </h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Scan Barcode di Kasir', sub: 'Isi barcode & scan cepat saat transaksi', icon: 'ri-barcode-line', color: 'success', to: { name: 'userguide-retail-barcode' } },
          { title: 'Harga Grosir Bertingkat', sub: 'Harga lebih murah untuk pembelian banyak', icon: 'ri-price-tag-3-line', color: 'warning', to: { name: 'userguide-retail-harga-grosir' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink
          :to="link.to"
          class="text-decoration-none"
        >
          <VCard
            variant="outlined"
            hover
          >
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar
                :color="link.color"
                variant="tonal"
                size="40"
                rounded
              >
                <VIcon
                  :icon="link.icon"
                  size="22"
                />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">
                  {{ link.title }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ link.sub }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
